/**
 * M3 Expressive Motion System
 * 
 * Based on Google's Material 3 Expressive guidelines:
 * - Spring-based physics for natural, fluid animations
 * - Emphasized easing for expressive transitions
 * - Standard easing for utility transitions
 * - Duration tokens from short (50ms) to extra-long (1000ms)
 * 
 * @see https://m3.material.io/styles/motion/overview
 * @see https://design.google/library/expressive-material-design-google-research
 */

// =============================================================================
// EASING CURVES
// =============================================================================

/**
 * Emphasized easing set - captures the expressive style of M3
 * Use for large, expressive transitions like FAB expansion, page transitions
 */
export const emphasizedEasing = {
  /** Full emphasized curve - for bidirectional motion */
  default: 'cubic-bezier(0.05, 0.7, 0.1, 1.0)',
  /** Decelerate - for entering elements (coming into view) */
  decelerate: 'cubic-bezier(0.05, 0.7, 0.1, 1.0)',
  /** Accelerate - for exiting elements (leaving view) */
  accelerate: 'cubic-bezier(0.3, 0.0, 0.8, 0.15)',
} as const;

/**
 * Standard easing set - for simple, small, or utility-focused transitions
 * Use for hover states, small toggles, subtle feedback
 */
export const standardEasing = {
  /** Full standard curve - for bidirectional motion */
  default: 'cubic-bezier(0.2, 0.0, 0, 1.0)',
  /** Decelerate - for entering elements */
  decelerate: 'cubic-bezier(0, 0, 0, 1)',
  /** Accelerate - for exiting elements */
  accelerate: 'cubic-bezier(0.3, 0, 1, 1)',
} as const;

/**
 * Spring-like easing for bouncy, playful effects
 * Use sparingly for hero moments and delightful interactions
 */
export const springEasing = {
  /** Gentle spring - subtle bounce */
  gentle: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  /** Medium spring - noticeable bounce */
  medium: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  /** Snappy spring - quick and energetic */
  snappy: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
} as const;

// =============================================================================
// DURATION TOKENS
// =============================================================================

/**
 * Short durations - for small utility-focused transitions
 * Selection controls, micro-interactions, hover states
 */
export const shortDuration = {
  short1: 50,
  short2: 100,
  short3: 150,
  short4: 200,
} as const;

/**
 * Medium durations - for transitions that traverse medium screen area
 * Button presses, card interactions, menu opening
 */
export const mediumDuration = {
  medium1: 250,
  medium2: 300,
  medium3: 350,
  medium4: 400,
} as const;

/**
 * Long durations - paired with Emphasized easing
 * Large expressive transitions, FAB expansion, modal dialogs
 */
export const longDuration = {
  long1: 450,
  long2: 500,
  long3: 550,
  long4: 600,
} as const;

/**
 * Extra-long durations - for ambient transitions without user input
 * Auto-advance carousels, background animations, loading states
 */
export const extraLongDuration = {
  extraLong1: 700,
  extraLong2: 800,
  extraLong3: 900,
  extraLong4: 1000,
} as const;

// =============================================================================
// COMBINED TRANSITION PRESETS
// =============================================================================

/**
 * Pre-built transition strings for common use cases
 * Format: 'property duration easing'
 */
export const transitions = {
  // Micro-interactions
  /** Hover/focus feedback on buttons */
  buttonHover: `all ${shortDuration.short3}ms ${standardEasing.default}`,
  /** Press/tap feedback */
  buttonPress: `transform ${shortDuration.short2}ms ${standardEasing.accelerate}`,
  /** Icon rotation (like settings gear) */
  iconRotate: `transform ${mediumDuration.medium2}ms ${emphasizedEasing.default}`,
  
  // Component state changes
  /** FAB state change (idle to active) */
  fabState: `all ${mediumDuration.medium3}ms ${emphasizedEasing.default}`,
  /** Mini-FAB popout animation */
  miniFabPopout: `all ${mediumDuration.medium2}ms ${springEasing.gentle}`,
  /** Chip selection */
  chipSelect: `all ${shortDuration.short4}ms ${standardEasing.default}`,
  
  // Layout transitions
  /** Card expansion */
  cardExpand: `all ${longDuration.long2}ms ${emphasizedEasing.default}`,
  /** Drawer/panel slide */
  drawerSlide: `transform ${mediumDuration.medium4}ms ${emphasizedEasing.decelerate}`,
  /** Modal/dialog entrance */
  modalEnter: `all ${mediumDuration.medium3}ms ${emphasizedEasing.decelerate}`,
  /** Modal/dialog exit */
  modalExit: `all ${shortDuration.short4}ms ${emphasizedEasing.accelerate}`,
  
  // Color and opacity
  /** Background color change */
  colorChange: `background-color ${mediumDuration.medium1}ms ${standardEasing.default}`,
  /** Opacity fade */
  fade: `opacity ${shortDuration.short3}ms ${standardEasing.default}`,
  /** Glow/shadow intensity */
  glowPulse: `box-shadow ${mediumDuration.medium2}ms ${standardEasing.default}`,
  
  // Typography
  /** Text emphasis change */
  textEmphasis: `all ${shortDuration.short3}ms ${standardEasing.default}`,
  
  // Hero moments (combine multiple properties)
  /** Success celebration - use with haptic + sound */
  heroSuccess: `all ${longDuration.long1}ms ${springEasing.gentle}`,
  /** Error shake feedback */
  heroError: `transform ${shortDuration.short4}ms ${springEasing.snappy}`,
} as const;

// =============================================================================
// KEYFRAME ANIMATION DEFINITIONS (for CSS-in-JS)
// =============================================================================

/**
 * Keyframe definitions for complex animations
 * Use with MUI's keyframes or styled-components
 */
export const keyframes = {
  /** Gentle pulse for attention */
  pulse: `
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.9; }
  `,
  
  /** Breathing glow effect */
  breathe: `
    0%, 100% { 
      box-shadow: 0 0 10px rgba(96, 165, 250, 0.3);
      transform: scale(1);
    }
    50% { 
      box-shadow: 0 0 25px rgba(96, 165, 250, 0.6);
      transform: scale(1.02);
    }
  `,
  
  /** Success checkmark draw */
  checkDraw: `
    0% { stroke-dashoffset: 100; }
    100% { stroke-dashoffset: 0; }
  `,
  
  /** Error shake */
  shake: `
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
    20%, 40%, 60%, 80% { transform: translateX(4px); }
  `,
  
  /** Ripple expand from center */
  ripple: `
    0% { transform: scale(0); opacity: 0.8; }
    100% { transform: scale(2.5); opacity: 0; }
  `,
  
  /** FAB spring entrance */
  fabSpring: `
    0% { transform: scale(0) rotate(-45deg); opacity: 0; }
    60% { transform: scale(1.1) rotate(5deg); opacity: 1; }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
  `,
  
  /** Mini-FAB cascade entrance (for popout menus) */
  miniFabCascade: (_index: number) => `
    0% { 
      transform: translateY(20px) scale(0.5); 
      opacity: 0; 
    }
    100% { 
      transform: translateY(0) scale(1); 
      opacity: 1; 
    }
  `,
  
  /** Slide up from bottom */
  slideUp: `
    0% { transform: translateY(100%); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  `,
  
  /** Slide down to bottom */
  slideDown: `
    0% { transform: translateY(0); opacity: 1; }
    100% { transform: translateY(100%); opacity: 0; }
  `,
  
  /** Rotate continuous (for loading, settings gear) */
  rotateContinuous: `
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `,
  
  /** Color shift for hero moments */
  colorShift: `
    0%, 100% { filter: hue-rotate(0deg); }
    50% { filter: hue-rotate(15deg); }
  `,
} as const;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Creates a staggered delay for cascading animations
 * @param index - Item index in the list
 * @param baseDelay - Base delay in ms (default: 50ms)
 * @returns Delay string for CSS
 */
export function staggerDelay(index: number, baseDelay: number = 50): string {
  return `${index * baseDelay}ms`;
}

/**
 * Creates a transition string with multiple properties
 * @param properties - Array of CSS properties to transition
 * @param duration - Duration in ms
 * @param easing - Easing curve
 */
export function multiPropertyTransition(
  properties: string[],
  duration: number = mediumDuration.medium2,
  easing: string = emphasizedEasing.default
): string {
  return properties.map(prop => `${prop} ${duration}ms ${easing}`).join(', ');
}

/**
 * Get appropriate duration based on distance traveled
 * M3 principle: longer distances = longer durations
 * @param distance - Approximate pixel distance
 */
export function getDurationForDistance(distance: number): number {
  if (distance < 100) return shortDuration.short4;
  if (distance < 300) return mediumDuration.medium2;
  if (distance < 500) return mediumDuration.medium4;
  return longDuration.long2;
}

// =============================================================================
// MUI SX PROP HELPERS
// =============================================================================

/**
 * MUI sx prop objects for common animations
 * Usage: <Box sx={{ ...m3Sx.buttonHover }} />
 */
export const m3Sx = {
  /** Button hover effect with scale */
  buttonHover: {
    transition: transitions.buttonHover,
    '&:hover': {
      transform: 'scale(1.02)',
    },
    '&:active': {
      transform: 'scale(0.98)',
      transition: transitions.buttonPress,
    },
  },
  
  /** FAB with spring animation */
  fabExpressive: {
    transition: transitions.fabState,
    '&:hover': {
      transform: 'scale(1.08)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
  },
  
  /** Card with lift on hover */
  cardLift: {
    transition: transitions.cardExpand,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
    },
  },
  
  /** Chip selection feedback */
  chipInteractive: {
    transition: transitions.chipSelect,
    '&:hover': {
      transform: 'scale(1.05)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
  },
  
  /** Icon button with rotation hint on hover */
  iconButtonExpressive: {
    transition: transitions.iconRotate,
    '&:hover': {
      transform: 'rotate(15deg) scale(1.1)',
    },
  },
  
  /** Pulse attention animation */
  pulseAttention: {
    animation: `pulse 2s ${emphasizedEasing.default} infinite`,
    '@keyframes pulse': {
      '0%, 100%': { transform: 'scale(1)', opacity: 1 },
      '50%': { transform: 'scale(1.05)', opacity: 0.9 },
    },
  },
  
  /** Breathing glow for active states */
  breathingGlow: {
    animation: `breathe 3s ${standardEasing.default} infinite`,
    '@keyframes breathe': {
      '0%, 100%': { 
        boxShadow: '0 0 10px rgba(96, 165, 250, 0.3)',
      },
      '50%': { 
        boxShadow: '0 0 25px rgba(96, 165, 250, 0.6)',
      },
    },
  },
} as const;

// =============================================================================
// EXPORTS
// =============================================================================

export const m3Motion = {
  easing: {
    emphasized: emphasizedEasing,
    standard: standardEasing,
    spring: springEasing,
  },
  duration: {
    short: shortDuration,
    medium: mediumDuration,
    long: longDuration,
    extraLong: extraLongDuration,
  },
  transitions,
  keyframes,
  sx: m3Sx,
  helpers: {
    staggerDelay,
    multiPropertyTransition,
    getDurationForDistance,
  },
};

export default m3Motion;
